import { isEqual } from 'lodash';

import type { TableRow } from '../table-store';

// PSTUDIO undo-memory fix — the row-level diff that a composite (group / paste /
// multi-insert / append / delete) undo command stores INSTEAD of two whole-table
// snapshots.
//
// Background: `_recordGroupMutation` used to capture `before = toJS(this.rows)`
// and `after = toJS(this.rows)` (two full deep clones of the ENTIRE table) and
// pin both in the command's execute/undo closures. With the 100-command cap
// that is up to 200 full-table clones retained at once → hundreds of MB on a
// large solution, the leading cause of "the editor gets heavy after a long
// session". The clones are almost entirely UNTOUCHED rows — pure waste, because
// undo/redo only has to move the handful of rows the operation actually changed.
//
// This module computes the minimal delta (touched rows only) and reconstructs
// the target row set from the LIVE table + the delta at undo/redo time. That is
// correct because the history stack is strictly LIFO (a command's undo/redo runs
// only while it is on top, i.e. the live table is exactly this command's
// `after` state), so the untouched rows in the live table already match the
// target's untouched rows — only the touched ones need swapping.

export interface RowChange {
  id: string;
  /** The row as it was in `before` (restored on undo). */
  before: TableRow;
  /** The row as it was in `after` (restored on redo). */
  after: TableRow;
}

export interface RowDelta {
  /** Rows present in BOTH snapshots whose content differs. */
  changed: RowChange[];
  /** Rows present in `after` but NOT in `before` (added by the operation). */
  added: TableRow[];
  /** Rows present in `before` but NOT in `after` (removed by the operation). */
  removed: TableRow[];
}

/**
 * Diff two row snapshots into the minimal set of touched rows. `before`/`after`
 * are the plain-object snapshots (already `toJS`-ed) captured around a mutation;
 * they are consumed transiently here and must NOT be retained by the caller —
 * only the returned (small) delta is kept in the undo command.
 */
export function computeRowDelta(
  before: TableRow[],
  after: TableRow[]
): RowDelta {
  const beforeById = new Map(before.map((r) => [r.id, r] as const));
  const afterIds = new Set(after.map((r) => r.id));

  const changed: RowChange[] = [];
  const added: TableRow[] = [];
  const removed: TableRow[] = [];

  for (const row of after) {
    const prev = beforeById.get(row.id);
    if (!prev) {
      added.push(row);
    } else if (!isEqual(prev, row)) {
      changed.push({ id: row.id, before: prev, after: row });
    }
  }
  for (const row of before) {
    if (!afterIds.has(row.id)) removed.push(row);
  }

  return { changed, added, removed };
}

/**
 * Reconstruct a target row set by applying `delta` to a `base` row set.
 *
 * - `dir === 'redo'`: `base` is the pre-operation (`before`) state → returns the
 *   post-operation (`after`) state.
 * - `dir === 'undo'`: `base` is the post-operation (`after`) state → returns the
 *   pre-operation (`before`) state.
 *
 * Untouched rows are carried through from `base` unchanged (preserving its array
 * order); re-added rows are appended. Downstream (`_restoreToSnapshot`) keys by
 * id and sorts the display by `.order`, so array position is irrelevant. The
 * `base` array and the delta objects are never mutated.
 */
export function applyRowDelta(
  base: TableRow[],
  delta: RowDelta,
  dir: 'undo' | 'redo'
): TableRow[] {
  const byId = new Map(base.map((r) => [r.id, r] as [string, TableRow]));

  if (dir === 'redo') {
    // before → after
    for (const c of delta.changed) byId.set(c.id, c.after);
    for (const r of delta.removed) byId.delete(r.id);
    for (const r of delta.added) byId.set(r.id, r);
  } else {
    // after → before
    for (const c of delta.changed) byId.set(c.id, c.before);
    for (const r of delta.added) byId.delete(r.id);
    for (const r of delta.removed) byId.set(r.id, r);
  }

  return [...byId.values()];
}
